/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useEffect, useMemo, useState, Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Button, Card, Col, Divider, Form, Input, Modal, Radio, Row, Select, Skeleton, Space, Typography } from 'antd';
import { CopyOutlined, WarningOutlined } from '@ant-design/icons';

// Misc imports
import { BOT_DEPLOYMENTS_CHANNELS } from '../../constants/main';
import { isEmptyArray } from '../../utils/is/isEmptyArray';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useParams } from 'react-router-dom';
import is from '../../utils/is';
import BotAvatar from '../UI/BotAvatar';
import { IS_WEBCHAT_DEPLOYMENT_APPLICATION_SETTINGS_ENABLED } from '../../constants/env';

// Constant declarations
const { Option } = Select;

const BotDeploymentsCloneModal = ({
  isModalOpen = false,
  onModalClose = () => {},
  onModalConfirm = () => {},
  loader = false,
  deploymentToClone = {}
}) => {
  const { orgId, tenantId } = useParams();
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [applicationSettings, setApplicationSettings] = useState();
  const [isWebchat, setIsWebchat] = useState(false);
  const { data: bots, isFetching: isFetchingBots } = getCurrentQueryState('fetchBots', { orgId });
  const [ currentStep, setCurrentStep ] = useState(0);

  useEffect(() => {
    if (deploymentToClone.platform === BOT_DEPLOYMENTS_CHANNELS.MICROSOFT_WEB_CHAT.key) {
      if (deploymentToClone?.settings?.integration) {
        setApplicationSettings('customApp');
      } else {
        setApplicationSettings('messageApp');
      }
      setIsWebchat(true);
    } else {
      setIsWebchat(false);
    }

    // Cleanup
    setCurrentStep(0);
    return () => form.resetFields();
  }, [deploymentToClone.id]);

  useEffect(() => {
    form.resetFields();
  }, [isWebchat]);

  // Form handlers
  const onFormSubmit = (values) => {

    const configuration = {
      displayName: form.getFieldValue(['configuration', 'displayName']),
      tenantId: form.getFieldValue(['configuration', 'tenantId'])
    };

    if (isWebchat && applicationSettings === 'messageApp') {
      onModalConfirm(configuration);
    } else {
      const valuesToSubmit = {
        ...configuration,
        settings: values
      };
      onModalConfirm(valuesToSubmit);
    }
  };

  const onFieldError = (fields) => {
    if (!isEmptyArray(fields.errorFields)) {
      form.scrollToField(fields.errorFields[0].name, { scrollMode: 'if-needed', behavior: 'smooth', block: 'center' });
    }
  };

  const renderIntegrationFields = () => {
    switch (deploymentToClone.platform) {
      case BOT_DEPLOYMENTS_CHANNELS.FACEBOOK.key :
        return (
          <Fragment>
            <Form.Item
              label={
                <Typography.Text
                  className="fs-base"
                >
                  {t('Page Access Token')}
                </Typography.Text>
              }
              name={['facebook', 'pageAccessToken']}
              rules={[{
                required: true,
                message: t('Please input Page Access Token')
              }]}
            >
              <Input placeholder={t('Set page access token')} />
            </Form.Item>

            <Form.Item
              label={
                <Typography.Text
                  className="fs-base"
                >
                  {t('Page ID')}
                </Typography.Text>
              }
              name={['facebook', 'pageId']}
              rules={[{
                required: true,
                message: t('Please input Page ID')
              }]}
            >
              <Input placeholder={t('Set page id')} />
            </Form.Item>
          </Fragment>
        );

      case BOT_DEPLOYMENTS_CHANNELS.VIBER.key :
        return (
          <Fragment>
            <Form.Item
              label={
                <Typography.Text
                  className="fs-base"
                >
                  {t('Authentication Token')}
                </Typography.Text>
              }
              name={['viber', 'authToken']}
              rules={[{
                required: true,
                message: t('Please input authentication token')
              }]}
            >
              <Input
                placeholder={t('Set authentication token')}
                allowClear
              />
            </Form.Item>
          </Fragment>
        );
      case BOT_DEPLOYMENTS_CHANNELS.WHATSAPP.key :
        return (
          <Fragment>
            <Form.Item
              label={
                <Typography.Text
                  className="fs-base"
                >
                  {t('Access Token')}
                </Typography.Text>
              }
              name={['whatsapp', 'accessToken']}
              rules={[{
                required: true,
                message: t('Please input access token')
              }]}
            >
              <Input
                placeholder={t('Set access token')}
                allowClear
              />
            </Form.Item>
            <Form.Item
              label={
                <Typography.Text
                  className="fs-base"
                >
                  {t('Phone Number ID')}
                </Typography.Text>
              }
              name={['whatsapp', 'phoneNumberId']}
              rules={[{
                required: true,
                message: t('Please input phone number ID')
              }]}
            >
              <Input
                placeholder={t('Set phone number ID')}
                allowClear
              />
            </Form.Item>
          </Fragment>
        );

      case BOT_DEPLOYMENTS_CHANNELS.MICROSOFT_TEAMS.key :
        return (
          <Fragment>
            <Form.Item
              label={
                <Typography.Text
                  className="fs-base"
                >
                  {t('Client ID')}
                </Typography.Text>
              }
              name={[...['microsoftTeams'], 'clientId']}
              rules={[{
                required: true,
                message: t('Please input Client ID')
              }]}
            >
              <Input
                placeholder={t('Set client id')}
                allowClear
              />
            </Form.Item>

            <Form.Item
              label={
                <Typography.Text
                  className="fs-base"
                >
                  {t('Client Secret')}
                </Typography.Text>
              }
              name={[...['microsoftTeams'], 'clientSecret']}
              rules={[{
                required: true,
                message: t('Please input Client Secret')
              }]}
            >
              <Input
                placeholder={t('Set client secret')}
                allowClear
              />
            </Form.Item>

            <Form.Item
              label={
                <Typography.Text
                  className="fs-base"
                >
                  {t('Azure Tenant ID')}
                </Typography.Text>
              }
              name={[...['microsoftTeams'], 'azureTenantId']}
              rules={[{
                required: true,
                message: t('Please input Azure Tenant Id')
              }]}
            >
              <Input
                placeholder={t('Set azure tenant id')}
                allowClear
              />
            </Form.Item>

            <Form.Item
              label={
                <Typography.Text
                  className="fs-base"
                >
                  {t('Service URL')}
                </Typography.Text>
              }
              name={[...['microsoftTeams'], 'serviceUrl']}
              rules={[{
                required: true,
                message: t('Please input Service URL')
              }]}
            >
              <Input
                placeholder={t('Set service url')}
                allowClear
              />
            </Form.Item>
          </Fragment>
        );

      case BOT_DEPLOYMENTS_CHANNELS.MICROSOFT_WEB_CHAT.key:
        return (
          <Fragment>
            <Form.Item className="mb-0">
              <Radio.Group
                className="mb-1"
                value={applicationSettings}
                onChange={(e) => {
                  if (e.target.value === 'customApp') {
                    form.setFieldsValue({
                      integration: deploymentToClone?.settings?.integration
                    });
                  }
                  setApplicationSettings(e.target.value);
                }}
              >
                <Space direction="vertical">
                  <Radio className="mb-2" value="messageApp">
                    <Typography.Text className="fs-base">
                      {t('Helvia Webchat App')}
                    </Typography.Text>
                  </Radio>

                  <Radio className="mb-2" value="customApp">
                    <Typography.Text
                      className="fs-base"
                    >
                      {t('My own Webchat App')}
                    </Typography.Text>
                  </Radio>
                </Space>
              </Radio.Group>

              {
                applicationSettings === 'customApp' ? (
                  <Card
                    size="small"
                    className="bg-layout-gray shadow-none"
                  >
                    <Form.Item
                      shouldUpdate
                    >
                      {
                        () => (
                          <Fragment>
                            <Form.Item
                              label={
                                <Typography.Text
                                  className="fs-base"
                                >
                                  {t('Client ID')}
                                </Typography.Text>
                              }
                              name={[...['microsoftWebChat'], 'clientId']}
                              rules={[{
                                required: applicationSettings !== 'messageApp',
                                message: t('Please input Client ID')
                              }]}
                            >
                              <Input
                                disabled={applicationSettings !== 'customApp'}
                                placeholder={t('Set client id')} />
                            </Form.Item>

                            <Form.Item
                              label={
                                <Typography.Text
                                  className="fs-base"
                                >
                                  {t('Client Secret')}
                                </Typography.Text>
                              }
                              name={[...['microsoftWebChat'], 'clientSecret']}
                              rules={[{
                                required: applicationSettings !== 'messageApp',
                                message: t('Please input Client Secret')
                              }]}
                            >
                              <Input
                                disabled={applicationSettings !== 'customApp'}
                                placeholder={t('Set client secret')} />

                            </Form.Item>

                            <Form.Item
                              label={
                                <Typography.Text
                                  className="fs-base"
                                >
                                  {t('Web Secret Key')}
                                </Typography.Text>
                              }
                              name={[...['microsoftWebChat'], 'webSecretKey']}
                              rules={[{
                                required: applicationSettings !== 'messageApp',
                                message: t('Please input Web Secret Key')
                              }]}
                            >
                              <Input
                                disabled={applicationSettings !== 'customApp'}
                                placeholder={t('Set web secret key')}
                              />
                            </Form.Item>

                            <Form.Item
                              label={
                                <Typography.Text
                                  className="fs-base"
                                >
                                  {t('Azure Agent Handle')}
                                </Typography.Text>
                              }
                              name={[...['microsoftWebChat'], 'azureBotHandle']}
                              rules={[{
                                required: applicationSettings !== 'messageApp',
                                message: t('Please input Azure Agent Handle')
                              }]}
                            >
                              <Input
                                disabled={applicationSettings !== 'customApp'}
                                placeholder={t('Set azure agent handle')} />
                            </Form.Item>
                          </Fragment>
                        )
                      }
                    </Form.Item>
                  </Card>
                ) : null
              }
            </Form.Item>
          </Fragment>
        );

      case BOT_DEPLOYMENTS_CHANNELS.SLACK.key :
        return (
          <Fragment>
            <Form.Item
              label={
                <Typography.Text
                  className="fs-base"
                >
                  {t('App ID')}
                </Typography.Text>
              }
              name={['slack', 'appId']}
              rules={[{
                required: true,
                message: t('Please input App ID')
              }]}
            >
              <Input
                placeholder={t('Set app id')}
                allowClear
              />
            </Form.Item>

            <Form.Item
              label={
                <Typography.Text
                  className="fs-base"
                >
                  {t('Client ID')}
                </Typography.Text>
              }
              name={['slack', 'clientId']}
              rules={[{
                required: true,
                message: t('Please input Client ID')
              }]}
            >
              <Input
                placeholder={t('Set client id')}
                allowClear
              />
            </Form.Item>

            <Form.Item
              label={
                <Typography.Text
                  className="fs-base"
                >
                  {t('Client Secret')}
                </Typography.Text>
              }
              name={['slack', 'clientSecret']}
              rules={[{
                required: true,
                message: t('Please input Client Secret')
              }]}
            >
              <Input
                placeholder={t('Set client secret')}
                allowClear
              />
            </Form.Item>

            <Form.Item
              label={
                <Typography.Text
                  className="fs-base"
                >
                  {t('Team ID')}
                </Typography.Text>
              }
              name={['slack', 'teamId']}
              rules={[{
                required: true,
                message: t('Please inputTeam ID')
              }]}
            >
              <Input
                placeholder={t('Set team id')}
                allowClear
              />
            </Form.Item>

            <Form.Item
              label={
                <Typography.Text
                  className="fs-base"
                >
                  {t('Agent User OAuth Token')}
                </Typography.Text>
              }
              name={['slack', 'botUserOAuthToken']}
              rules={[{
                required: true,
                message: t('Please input Agent User OAuth Token')
              }]}
            >
              <Input
                placeholder={t('Set agent user oauth token')}
                allowClear
              />
            </Form.Item>
          </Fragment>
        );

      default:
        break;
    }
  };

  const steps = useMemo(() => [
    {
      title: t('TARGET'),
      content: (
        <Row gutter={16} className="mb-3">
          <Col xs={24}>
            <Form.Item
              name={['configuration', 'displayName']}
              label={t('Cloned Deployment Name')}
              labelCol={{ span: 24 }}
            >
              <Input
                size="large"
                autoFocus={true}
                onFocus={(e) => e.target.select()}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              name={['configuration', 'tenantId']}
              label={t('Cloned Deployment will be added to this agent')}
              labelCol={{ span: 24 }}
            >
              <Select
                placeholder={t('Choose an agent')}
                size="large"
                disabled={isFetchingBots}
                showSearch
                filterOption={(input, option) =>
                  bots && is.array(bots.items) && bots.items.find(bot => bot.id === option.value).displayName.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {
                  // The dataset is all bots, minus the bots that were added already
                  bots && is.array(bots.items) && [...bots.items].sort((a, b) => a.displayName.localeCompare(b.displayName)).map(bot => (
                    <Option key={bot.id} value={bot.id}>
                      {
                        isFetchingBots ? (
                          <Skeleton active title={{ width: '80%' }} paragraph={false} className="mt-3 border-radius-base" />
                        ) : (
                          <BotAvatar bot={bot} avatarSize={24} />
                        )
                      }
                    </Option>
                  ))
                }
              </Select>
            </Form.Item>
          </Col>
        </Row>
      )
    },
    {
      title: t('SETTINGS'),
      content: (
        <Row className="justify-content-center">
          <Col span={24}>
            {
              renderIntegrationFields()
            }
          </Col>
        </Row>
      )
    }

  ]);

  const renderFooter = () => {
    return (
      <Row justify="end" align={'middle'} gutter={16}>
        {
          isWebchat && currentStep !== 0
            ? <Col span={12} hidden={!isWebchat}>
              <Typography.Text className="fs-sm">
                <Row gutter={16} className="align-items-baseline">
                  <Col span={3} className="d-flex align-items-center justify-content-end">
                    <WarningOutlined className="text-warning"/>
                  </Col>
                  <Col span={21}>
                    <Typography.Paragraph className={`fs-sm text-muted text-left`}>
                      <Trans t={t}>Note that after “Create Clone” you won’t be able to change your <Typography.Text className="ml-1 mr-1 fw-bold">Webchat app selection</Typography.Text> for this Deployment.</Trans>
                    </Typography.Paragraph>
                  </Col>
                </Row>
              </Typography.Text>
            </Col> : null
        }

        <Col>
          {
            currentStep !== 0 && (
              <Button type="text" size="large" className="mr-2" onClick={() => goToPreviousStep()}>
                {t('BACK')}
              </Button>
            )
          }

          {
            currentStep === steps.length - 1 || ( isWebchat && !IS_WEBCHAT_DEPLOYMENT_APPLICATION_SETTINGS_ENABLED) ? (
              <Button icon={<CopyOutlined />} type="primary" size="large" htmlType="submit">
                {t('Create Clone')}
              </Button>
            ) : (
              <Button
                type="primary"
                size="large"
                onClick={(e) => {
                  e.preventDefault(); // Prevent form submission
                  goToNextStep();
                }}
              >
                {t('Next Step')}
              </Button>
            )
          }
        </Col>
      </Row>
    );
  };

  const goToStep = (stepNumber) => {
    setCurrentStep(stepNumber);
  };

  const goToNextStep = () => {
    goToStep(currentStep + 1);
  };

  const goToPreviousStep = () => {
    goToStep(currentStep - 1);
  };

  return (

    <Modal
      title={
        <Fragment>
          <Typography.Paragraph className="d-flex mb-0">
            <span className="fs-xl mr-1">
              {Object.values(BOT_DEPLOYMENTS_CHANNELS).find((channel) => channel.key === deploymentToClone.platform)?.icon}
            </span>

            <span className="fs-xl">
              {`${t('Clone')} ${deploymentToClone.displayName}`}
            </span>
          </Typography.Paragraph>

          <Typography.Paragraph className="fs-lg fw-normal text-muted mb-0 mt-2">
            {
              isWebchat && !IS_WEBCHAT_DEPLOYMENT_APPLICATION_SETTINGS_ENABLED ? (
              `${t('Keep existing or enter new application settings')}`
              ) : (
              `${currentStep === 0 ? t('Step 1: Deployment Name & Agent') : !isWebchat ? t('Step 2: Enter your application settings') : t('Step 2: Keep existing or enter new application settings')}`
              )
            }
          </Typography.Paragraph>
        </Fragment>
      }
      footer={null}
      open={isModalOpen}
      destroyOnClose
      onCancel={onModalClose}
      centered
    >
      <Form
        name="cloneBotDeploymentsForm"
        layout="vertical"
        onFinish={onFormSubmit}
        onFinishFailed={onFieldError}
        form={form}
        initialValues={isWebchat ? {
          microsoftWebChat: applicationSettings === 'customApp' ? deploymentToClone?.settings?.integration?.microsoftWebChat : {},
          configuration: {
            displayName: `${deploymentToClone?.displayName} (copy)`,
            tenantId: tenantId
          }
        } : {
          configuration: {
            displayName: `${deploymentToClone?.displayName} (copy)`,
            tenantId: tenantId
          }
        }
        }
      >

        {steps[currentStep].content}
        <Divider />
        {renderFooter()}
      </Form>
    </Modal>
  );
};

BotDeploymentsCloneModal.propTypes = {
  isModalOpen: PropTypes.bool,
  onModalClose: PropTypes.func,
  onModalConfirm: PropTypes.func,
  loader: PropTypes.bool,
  deploymentToClone: PropTypes.object
};

export default BotDeploymentsCloneModal;
